import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, VStack, useToast } from "@chakra-ui/react";
import CustomLoader from "../../components/placeholders/CustomLoader";
import AudioCard from "../../components/cards/AudioCard";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import SearchBar from "../../components/UI/SearchBar";
import { useRecordings } from "../../context/RecordingContext";
import { Recording } from "../../model/recording";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import EmptyState from "../../components/placeholders/EmptyState";
import { HandsPrayingIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useSharedRecordingQuery } from "../../hooks/useSharedRecordingQuery";

const PAGE_SIZE = 10;

const ZoomPage: React.FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    hasInitialized,
    setHasInitialized,
    recordingsByCategory,
    hasLoadedCategory,
    hasLoadedFavoriteIDs,
    listFavoriteIDs,
    listRecordingsByCategory,
    addFavorite,
    removeFavorite,
    isFavorite,
    getRecordingFiles,
  } = useRecordings();

  const {
    searchMode,
    searchQuery,
    setSearchQuery,
    sharedRecording,
    isLoadingSharedRecording,
    selectSharedRecording,
  } = useSharedRecordingQuery({
    matchRecording: (recording: Recording) =>
      recording.category === "zoom_prayer",
    onLoadError: (error: unknown) => {
      toast({
        title: t("pages.zoomPrayer.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const recordings = useMemo(
    () => recordingsByCategory.zoom_prayer ?? [],
    [recordingsByCategory.zoom_prayer],
  );

  const loadRecordingsPage = useCallback(
    async (reset: boolean) => {
      if (reset) {
        setIsFetching(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const result = await listRecordingsByCategory("zoom_prayer", {
          limit: PAGE_SIZE,
          nextPageToken: reset ? undefined : nextPageToken,
        });
        setNextPageToken(result.nextPageToken);
      } finally {
        setIsFetching(false);
        setIsLoadingMore(false);
      }
    },
    [listRecordingsByCategory, nextPageToken],
  );

  useEffect(() => {
    if (hasInitialized.zoom_prayer) {
      return;
    }

    let isMounted = true;

    const initialize = async () => {
      try {
        if (!hasLoadedFavoriteIDs) {
          await listFavoriteIDs();
        }

        if (!hasLoadedCategory("zoom_prayer")) {
          await loadRecordingsPage(true);
        }
      } catch (error) {
        toast({
          title: t("pages.zoomPrayer.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted) {
          setHasInitialized((prev) => ({ ...prev, zoom_prayer: true }));
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
  }, [
    hasInitialized.zoom_prayer,
    hasLoadedCategory,
    hasLoadedFavoriteIDs,
    listFavoriteIDs,
    loadRecordingsPage,
    setHasInitialized,
    t,
    toast,
  ]);

  const displayedRecordings = useMemo(
    () => (sharedRecording ? [sharedRecording] : recordings),
    [recordings, sharedRecording],
  );

  const hasNoData = !sharedRecording && recordings.length === 0;
  const hasNoResults =
    sharedRecording !== null && displayedRecordings.length === 0;

  const handleLoadMore = async () => {
    if (!nextPageToken || isLoadingMore) {
      return;
    }

    try {
      await loadRecordingsPage(false);
    } catch (error) {
      toast({
        title: t("pages.zoomPrayer.loadErrorTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <VStack w={DEFAULT_WIDTH} mx="auto" px={PAGE_PADDING} align="start">
      <SearchBar
        value={searchQuery}
        onSearch={setSearchQuery}
        placeholder={String(t("pages.zoomPrayer.searchPlaceholder"))}
        mode={searchMode}
        mb={7}
        onSuggestionSelect={(item) => selectSharedRecording(item.recordingID)}
      />
      {isFetching || isLoadingSharedRecording || !hasInitialized.zoom_prayer ? (
        <CustomLoader />
      ) : hasNoData ? (
        <EmptyState
          icon={HandsPrayingIcon}
          title={t("pages.zoomPrayer.emptyTitle")}
          description={t("pages.zoomPrayer.emptyDescription")}
        />
      ) : hasNoResults ? (
        <EmptyState
          icon={HandsPrayingIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full">
          {displayedRecordings.map((recording) => (
            <AudioCard
              key={recording.recordingID}
              recordingID={recording.recordingID}
              title={recording.title}
              preacherName={recording.preacherName}
              date={formatDate(recording.date)}
              hasRecording={Boolean(recording.recordingS3Key)}
              hasSummary={Boolean(recording.summaryS3Key)}
              youtubeLink={recording.youtubeLink}
              saved={isFavorite(recording.recordingID)}
              onAddFavorite={() =>
                addFavorite(recording.recordingID, recording)
              }
              onRemoveFavorite={removeFavorite}
              onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
            />
          ))}

          {!sharedRecording && nextPageToken && (
            <Button
              mt={2}
              alignSelf="center"
              onClick={handleLoadMore}
              isLoading={isLoadingMore}
            >
              {t("common.loadMore")}
            </Button>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default ZoomPage;
